import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FileText, Search, WifiOff, Globe, Share2 } from 'lucide-react';
import { Reveal, Stagger, StaggerItem, EASE } from './LandingMotion';

export const DocumentSection = () => {
  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const reduce = useReducedMotion();

  // Reduced-motion-safe pop for the orange "CACHED" badges + translation arrow:
  // collapse the scale/rise to a plain fade when the user prefers reduced motion.
  const popInitial = reduce ? { opacity: 0 } : { opacity: 0, scale: 0.6 };
  const popVisible = reduce ? { opacity: 1 } : { opacity: 1, scale: 1 };
  const arrowInitial = reduce ? { opacity: 0 } : { opacity: 0, scale: 0.5 };

  // Simulated typing effect
  useEffect(() => {
    const text = 'Crane lift plan T-4';
    let i = 0;
    const typing = setInterval(() => {
      if (i < text.length) {
        setSearchText(text.slice(0, i + 1));
        i++;
      } else {
        clearInterval(typing);
        setTimeout(() => setSearchResult(true), 500);
      }
    }, 100);
    return () => clearInterval(typing);
  }, []);

  return (
    <section className="py-24 md:py-32 relative bg-white">
      <div className="w-full max-w-[1200px] mx-auto px-6">

        {/* Header */}
        <Reveal blur className="max-w-3xl mb-16">
          <div className="flex items-center gap-2 text-[#FF6A00] font-mono text-xs uppercase tracking-widest mb-4">
            <FileText size={14} />
            <span>Document Intelligence</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#1C1C1E] mb-6">
            Every document, <br />
            <span className="text-slate-500">in every worker’s hand.</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed font-light max-w-2xl">
            Upload once — VH organizes everything. Bolt gives your foremen a command center
            in their pocket, so permits, SOPs, and submittals are one tap away, even offline.
          </p>
        </Reveal>

        {/* Bento Grid Layout */}
        <Stagger className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6 h-auto lg:h-[600px]" stagger={0.1} delayChildren={0.05}>

          {/* Card 1: Instant Search (Large) */}
          <StaggerItem
            className="col-span-1 md:col-span-6 lg:col-span-7 bg-white rounded-3xl vh-edge-light p-8 relative overflow-hidden"
            whileHover={{ y: -4, borderColor: 'rgba(0,0,0,0.12)' }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF6A00]/10 blur-[80px] rounded-full pointer-events-none" />

            <div className="relative z-10 h-full flex flex-col">
              <div className="mb-auto">
                <h3 className="text-2xl font-bold text-[#1C1C1E] mb-2">Instant Recall</h3>
                <p className="text-slate-600 max-w-sm">Search across permits, blueprints, and RFIs instantly — even with fuzzy matching.</p>
              </div>

              {/* Mock Search UI */}
              <div className="mt-8 bg-white rounded-xl border border-black/10 p-4 shadow-2xl">
                <div className="flex items-center gap-3 border-b border-black/10 pb-3 mb-3">
                  <Search className="text-slate-500" size={18} />
                  <div className="font-mono text-slate-600 flex items-center">
                    {searchText}
                    <span className="w-2 h-4 bg-[#FF6A00] ml-1 animate-pulse" />
                  </div>
                </div>

                <div className="space-y-2 h-[140px]">
                  <AnimatePresence>
                    {searchResult && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-black/[0.04] hover:bg-black/[0.06] cursor-pointer border border-black/[0.06]"
                      >
                        <div className="p-2 bg-[#FF6A00]/15 text-[#FF6A00] rounded-lg"><FileText size={16} /></div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-[#1C1C1E]">Lift_Plan_Tower_4_v2.pdf</div>
                          <div className="text-xs text-slate-500">Updated 2h ago • 4.2 MB</div>
                        </div>
                        <div className="text-xs font-mono text-slate-500">P-8821</div>
                      </motion.div>
                    )}
                    {searchResult && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-transparent hover:bg-black/[0.04] cursor-pointer opacity-60"
                      >
                        <div className="p-2 bg-black/[0.06] text-slate-600 rounded-lg"><FileText size={16} /></div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-[#1C1C1E]">Crane_Safety_Specs.pdf</div>
                          <div className="text-xs text-slate-500">Ref Manual • 12 MB</div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </StaggerItem>

          {/* Card 2: Offline Mode (Tall) */}
          <StaggerItem
            className="col-span-1 md:col-span-3 lg:col-span-5 bg-white rounded-3xl vh-edge-light p-8 relative overflow-hidden flex flex-col justify-between"
            whileHover={{ y: -4, borderColor: 'rgba(0,0,0,0.12)' }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            <div>
              <div className="w-12 h-12 rounded-xl bg-[#FF6A00]/10 flex items-center justify-center mb-6 border border-[#FF6A00]/20">
                <WifiOff className="text-[#FF6A00]" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-[#1C1C1E] mb-2">Offline First</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                No signal? No problem. Bolt intelligently caches critical docs based on the worker’s scope and location.
              </p>
            </div>

            <Stagger className="mt-8 space-y-3" stagger={0.1} delayChildren={0.15}>
              {['Sub-basement B2', 'Remote Solar Field', 'Tunnel Section 4'].map((loc) => (
                <StaggerItem key={loc} y={12} className="flex items-center justify-between p-3 rounded-lg bg-black/[0.04] border border-black/[0.06]">
                  <span className="text-sm text-slate-600">{loc}</span>
                  <motion.span
                    className="text-[10px] font-bold text-[#FF6A00] bg-[#FF6A00]/10 px-2 py-0.5 rounded-full"
                    initial={popInitial}
                    whileInView={popVisible}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.35, ease: EASE, delay: reduce ? 0 : 0.45 }}
                  >
                    CACHED
                  </motion.span>
                </StaggerItem>
              ))}
            </Stagger>
          </StaggerItem>

          {/* Card 3: Translation (Wide) */}
          <StaggerItem
            className="col-span-1 md:col-span-3 lg:col-span-5 bg-white rounded-3xl vh-edge-light p-8 relative overflow-hidden"
            whileHover={{ y: -4, borderColor: 'rgba(0,0,0,0.12)' }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-[#1C1C1E] mb-2">Universal Access</h3>
                <p className="text-slate-600 text-sm">Tap-to-translate for bilingual crews.</p>
              </div>
              <Globe className="text-[#FF6A00]" size={24} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Reveal y={16} blur={false} duration={0.55} delay={0.1} className="p-4 rounded-xl bg-black/[0.04] border border-black/10">
                <p className="text-[10px] uppercase text-slate-500 mb-1">Source (EN)</p>
                <p className="text-sm text-[#1C1C1E] font-medium">Wear safety harness.</p>
              </Reveal>
              <Reveal y={16} blur={false} duration={0.55} delay={0.45} className="p-4 rounded-xl bg-[#FF6A00]/10 border border-[#FF6A00]/20 relative">
                <motion.span
                  className="absolute top-1/2 -left-3 text-[#FF6A00] transform -translate-y-1/2"
                  initial={arrowInitial}
                  whileInView={popVisible}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.3, ease: EASE, delay: reduce ? 0 : 0.7 }}
                >
                  <ArrowIcon />
                </motion.span>
                <p className="text-[10px] uppercase text-[#FF6A00] mb-1">Target (ES)</p>
                <p className="text-sm text-[#1C1C1E] font-medium">Use arnés de seguridad.</p>
              </Reveal>
            </div>
          </StaggerItem>

          {/* Card 4: Audit Trail (Small) */}
          <StaggerItem
            className="col-span-1 md:col-span-3 lg:col-span-7 bg-white rounded-3xl vh-edge-light p-8 flex items-center justify-between hover:border-black/15 transition-colors"
            whileHover={{ y: -4 }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FF6A00]/10 border border-[#FF6A00]/20 flex items-center justify-center text-[#FF6A00]">
                <Share2 size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#1C1C1E]">Audit & Share</h3>
                <p className="text-slate-600 text-sm">Track who opened what, when.</p>
              </div>
            </div>
            <div className="flex -space-x-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-8 h-8 rounded-full bg-slate-700 border-2 border-white flex items-center justify-center text-[10px] text-white font-bold">
                  {i === 3 ? '+4' : ''}
                </div>
              ))}
            </div>
          </StaggerItem>

        </Stagger>
      </div>
    </section>
  );
};

const ArrowIcon = ({ className = '' }) => (
  <svg className={`w-4 h-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
);
